import { Dispatch, SetStateAction } from 'react';

import { Nullable } from '@rbi-ctg/frontend';
import { IPrices } from '@rbi-ctg/menu';
import { IStore, IStoreAddress } from '@rbi-ctg/store';
import { ServiceModeStatus } from 'hooks/use-service-mode-status';
import { ServiceMode } from 'state/service-mode/types';

/**
 * The possible states of a restaurant based on:
 * 1. Availability (whether the restaurant and the restaurants service mode is configured correctly)
 * 2. The hours of the store
 */
export enum StorePermissions {
  OPEN_AND_AVAILABLE = 'openAndAvailable',
  OPEN_AND_UNAVAILABLE = 'openAndUnavailable',
  CLOSED = 'closed',
  NO_STORE_SELECTED = 'noStoreSelected'
}

// Type of store for Store Selection 1.0
export type SelectedStore = IStore & { hasSelection: true };
export type SelectedUnavailableStore = {
  hasSelection: false;
  physicalAddress: IStoreAddress;
  selectedUnavailableStoreName: string;
  chaseMerchantId: '';
  // Allows us to show/query data for a store that is unavailable
  selectedUnavailableStoreNumber: null | string;
};

// Type of store for Store Selection 1.0
// Store Selection 2.0 uses the IStore type
export type StoreState = SelectedStore | SelectedUnavailableStore;

// Type of store for Store Selection 2.0
export type Store = IStore | null;

export type GetStoreInformation = <T extends keyof IStore>(property: T) => IStore[T] | null;

export interface IStoreContext {
  disableChickenSandwich: boolean;
  email: string;
  checkIfRestaurantPosIsOnline: (storeId: string) => Promise<boolean>;
  isRestaurantAvailable(restaurant: StoreState): Promise<boolean>;
  openOrderingUnavailableDialog(): void;
  prices: IPrices;
  resetStore(): void;
  selectStore(store: IStore, hasCartItems: boolean, callback: () => void): Promise<void>;
  fetchStore: (storeId: string) => Promise<void>;
  setStore: Dispatch<SetStateAction<StoreState>>;
  /** {@link:Store} is the store type for store selection 2.0 {@link:docs/design-reviews/store-selection-2.0/README.md} */
  store: StoreState;
  resetLastTimeStoreUpdated(currentTime?: number): void;
  serviceModeStatus: ServiceModeStatus;
  selectUnavailableStore: (store: Partial<StoreState>) => void;
  getCurrentStoreStatus: (serviceMode?: ServiceMode) => StorePermissions;
  isStoreOpenAndAvailable: boolean;
  isStoreOpenAndUnavailable: boolean;
  isStoreClosed: boolean;
  noStoreSelected: boolean;
  getStoreInformation: GetStoreInformation;
}

export interface IPreloadedStoreState {
  prices: IPrices;
  store: StoreState;
}

export interface IPreloadedStoreState2_0 {
  prices: IPrices;
  store: IStore;
}

export type IStoreHook = {
  onConfirmStoreChange({ newStore, callback }: { newStore: IStore; callback: () => void }): void;
  getStoreInformation: GetStoreInformation;
} & Pick<IStoreContext, 'resetStore' | 'selectUnavailableStore' | 'setStore' | 'store'>;

/** Store selection 1.0 hook type */
export type IUseStore = IStoreHook & { store: StoreState };

/** Store selection 2.0 hook type */
export type IUseAnyStore = IStoreHook & { store: Store };

export type curriedGetStoreStatus = (args: {
  store: StoreState | Store;
  serviceModeStatuses: ServiceModeStatus;
  selectedServiceMode?: Nullable<ServiceMode>;
}) => (serviceMode?: Nullable<ServiceMode>) => StorePermissions;
